import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectLoggedInUserId } from '../../../../../Store/features/user/userSlice';
import { toast } from 'react-hot-toast';
import Navbar from '../../../../Navbar/Navbar';
import Footer from '../../../../Footer/Footer';
import SharePopup from "../../../../PopUp/SocialSharePopup/SharePopup";
import PurchasePopup from "../../../../Shared/PurchasePopup/PurchasePopup";
import { FaShare, FaTrashCan, FaPen } from "react-icons/fa6";
import { getProductByIdAPI, getCommunityByIdAPI, deleteProductFromCommunity, buyProductFromCommunity, joinCommunity } from '../../../../../Service/user';
import { load } from "@cashfreepayments/cashfree-js";
import { createPaymentSession, verifyPayment } from "../../../../../Service/user";
import './Pages.scss';
import AddProductPopup from '../AddProductPopup';
const SkeletonLoader = () => (
  <div className="service-page">
    <Navbar />
    <div className="page-content">
      <div className="community-header">
        <div className="community-info">
          <div className="skeleton community-image" />
          <div className="community-details">
            <div className="skeleton title" />
            <div className="skeleton description" />
            <div className="skeleton description" />
          </div>
        </div>
      </div>

      <div className="product-content">
        <div className="content-header">
          <div className="skeleton title" />
          <div className="meta-info">
            <div className="skeleton badge" />
          </div>
        </div>

        <div className="skeleton product-image" />

        <div className="description-section">
          <div className="skeleton title" />
          <div className="skeleton content-block" />
        </div>

        <div className="topics-section">
          <div className="skeleton title" />
          <div className="topics">
            <div className="skeleton topic" />
            <div className="skeleton topic" />
            <div className="skeleton topic" />
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

const ProductPage = () => {
  const { communityId, productId } = useParams();
  const navigate = useNavigate();
  const loggedInUserId = useSelector(selectLoggedInUserId);
  
  const [product, setProduct] = useState(null);
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showUrlsPopup, setShowUrlsPopup] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isProduct, setIsProduct] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const isCommunityMember = useMemo(() => {
    return community?.members?.some(
      (member) => member.member._id === loggedInUserId
    );
  }, [community?.members, loggedInUserId]);

  useEffect(() => {
    fetchData();
  }, [communityId, productId]);

  const fetchData = async () => {
    try {
      const [productResponse, communityResponse] = await Promise.all([
        getProductByIdAPI(communityId, productId),
        getCommunityByIdAPI(communityId)
      ]);
     
      setProduct(productResponse);
      setCommunity(communityResponse.data);

      // Check if user is admin
      const isUserAdmin = communityResponse.data.adminId._id === loggedInUserId;
      setIsAdmin(isUserAdmin);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProductFromCommunity(communityId, product._id);
        toast.success('Product deleted successfully');
        navigate(`/community/${communityId}`);
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const handleUpdate = () => {
    setShowEditPopup(true);
  };

  const handleEditComplete = (updatedProduct) => {
    setShowEditPopup(false);
    setProduct(updatedProduct);
    toast.success('Product updated successfully');
  };

  const initializeCashfree = async () => {
    try {
      return await load({ mode: "production" });
    } catch (error) {
      console.error("Failed to initialize Cashfree:", error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (userDetails) => {
    try {
      setIsPurchasing(true);
      toast.loading('Processing your purchase...');
      
      const cashfree = await initializeCashfree();
      if(isProduct){
      const paymentResponse = await createPaymentSession({
        name: userDetails.name,
        email: userDetails.email,
        mobile: userDetails.mobile,
        amount: product.amount,
        entityId: product._id,
        entityType: "product",
      });

      const { order_id, payment_session_id } = paymentResponse.data;

      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_modal",
      });

      const verificationResponse = await verifyPayment({
        orderId: order_id,
        entityId: product._id,
        entityType: "product",
      });

      if (verificationResponse.data.status === "SUCCESS") {
        await buyProductFromCommunity(communityId, product._id);
        toast.dismiss();
        toast.success("Purchase successful! You can now access the resource.");
        fetchData(); 
      } else {
        toast.dismiss();
        toast.error("Payment verification failed. Please try again.");
      }
      }
      else {
        const paymentResponse = await createPaymentSession({
          name: userDetails.name,
          email: userDetails.email,
          mobile: userDetails.mobile,
          amount: community.amount,
          entityId: community._id,
          entityType: "community",
        });

        const { order_id, payment_session_id } = paymentResponse.data;

        await cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal",
        });

        const verificationResponse = await verifyPayment({
          orderId: order_id,
          entityId: community._id,
          entityType: "community",
        });

        if (verificationResponse.data.status === "SUCCESS") {
          await joinCommunity(communityId, { memberIds: [loggedInUserId] });
          toast.dismiss();
          toast.success("Community joined successfully");
          fetchData(); 
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.dismiss();
      toast.error("Payment process failed. Please try again.");
    } finally {
      setIsPurchasing(false);
      setShowPaymentModal(false);
    }
  };

  const handleBuy = async () => {
    setIsProduct(true);
    if (product.amount > 0) {
      setShowPaymentModal(true);
    } else {
      try {
        setIsPurchasing(true);
        toast.loading('Processing your request...');
        await buyProductFromCommunity(communityId, product._id);
        toast.dismiss();
        toast.success('Product acquired successfully!');
        handleAccess();
        fetchData(); // Refresh data to update purchase status
      } catch (error) {
        console.error('Error acquiring product:', error);
        toast.dismiss();
        toast.error('Failed to acquire product. Please try again.');
      } finally {
        setIsPurchasing(false);
      }
    }
  };

  const handleAccess = () => {
    if (product?.URLS?.length > 0) {
      setShowUrlsPopup(true);
    } else {
      toast.error('No resources available');
    }
  };

  const handleJoinCommunity = async () => {
   
   setIsProduct(false);
    if (!community.isOpen) {
        toast.error("This community is closed for new members");
        return;
    }

    if (community.subscription === "paid") {
        setShowPaymentModal(true);
        return;
    }

    // Check if the user is already a member
    if (
        community.members.some((member) => member.member?._id === loggedInUserId)
        
    ) {
        toast.error("You are already a member of this community");
        return;
    }
    
    try { 
        setIsJoining(true);
        await joinCommunity(communityId, { memberIds: [loggedInUserId] });
        toast.success('Successfully joined the community!');
        fetchData();
    } catch (error) {
        console.error('Error joining community:', error);
        toast.error('Failed to join community');
    } finally {
        setIsJoining(false);
    }
  };

  const renderJoinButton = () => {
    if (!loggedInUserId) {
      return (
        <button 
          onClick={() => navigate('/login', { state: { from: window.location.pathname } })}
          className="join-button"
        >
          Login to Join Community
        </button>
      );
    }

    if (isAdmin) {
      return (
        <button 
          onClick={() => navigate(`/community/${communityId}`, { state: { activeTab: 'settings' } })}
          className="join-button manage-button"
        >
          Manage Community
        </button>
      );
    }

    if (isCommunityMember) {
      return (
        <button 
          onClick={() => navigate(`/community/${communityId}`)}
          className="join-button explore-button"
        >
          Explore Community
        </button>
      );
    }

    return (
      <button 
        onClick={handleJoinCommunity} 
        disabled={isJoining}
        className="join-button"
      >
        {isJoining ? 'Joining...' : 'Join Community'}
      </button>
    );
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  const isPurchased = product?.purchased_by?.includes(loggedInUserId);

  return (
    <div className="service-page product-page">
      <Navbar />
      
      <div className="page-content">
        <div className="community-header">
          <div className="community-info">
            <img 
              src={community?.image || '/default-community-image.png'} 
              alt={community?.name}
              className="community-image"
            />
            <div className="community-details">
              <h2>{community?.name}</h2>
              <p><div dangerouslySetInnerHTML={{ __html: community?.about }} /></p>
              {renderJoinButton()}
            </div>
          </div>
        </div>

        <div className="product-content">
          <div className="content-header">
            <h1>{product?.name}</h1>
            <div className="meta-info">
              <span className={`price-badge ${product?.is_free ? "free" : "paid"}`}>
                {product?.is_free ? "Free" : `₹${product?.amount}`}
              </span>
            </div>
          </div>

          <div className="product-image-section">
            <img src={product?.image} alt={product?.name} />
          </div>

          <div className="description-section">
            <div className="section-title">Description</div>
            <div className="content">
              <div dangerouslySetInnerHTML={{ __html: product?.description }} />
            </div>
          </div>

          {/* Admin Actions */}
          {isAdmin && (
            <div className="admin-actions">
              <button className="access-button" onClick={handleAccess}>
                Access Resource
              </button>
              <button className="edit-button" onClick={handleUpdate}>
                <FaPen size={20} /> Edit Product
              </button>
              <button className="delete-button" onClick={handleDelete}>
                <FaTrashCan size={20} /> Delete Product
              </button>
            </div>
          )}

          {/* User Actions */}
          {!isAdmin && (
            <div className="action-section">
              {!loggedInUserId ? (
                <button 
                  className="access-button" 
                  onClick={() => navigate('/login', { state: { from: window.location.pathname } })}
                >
                  Login to Access
                </button>
              ) : !isCommunityMember ? (
                <button 
                  className="access-button" 
                  onClick={handleJoinCommunity}
                  disabled={isJoining}
                >
                  {isJoining ? 'Joining...' : 'Join Community to Access'}
                </button>
              ) : !isPurchased ? (
                <button 
                  className="access-button" 
                  onClick={handleBuy}
                  disabled={isPurchasing}
                >
                  {isPurchasing 
                    ? 'Processing...' 
                    : product?.is_free 
                      ? "Get for Free" 
                      : `Buy for ₹${product?.amount}`
                  }
                </button>
              ) : (
                <button className="access-button" onClick={handleAccess}>
                  Access Resource
                </button>
              )}
            </div>
          )}

          <button 
            className="share-button"
            onClick={() => setSharePopupOpen(true)}
          >
            <FaShare /> Share
          </button>
        </div>
      </div>

      {showPaymentModal && (
        isProduct ? (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "product",
            name: product?.name,
            description: product?.description,
            amount: product?.amount,
            is_free: product?.is_free,
          }}
          loggedInUser={loggedInUser}
          onProceed={handlePaymentSuccess}
        />
      ) : (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "community",
            name: community?.name,
            description: community?.about,
            memberCount: community?.members.length,
            isOpen: community?.isOpen,
            amount: community?.amount,
            is_free: community?.subscription === "free",
        }}
        loggedInUser={loggedInUser}
        onProceed={handlePaymentSuccess}
        />
      ))}

      {sharePopupOpen && (
        <SharePopup
          postId={product?._id}
          isOpen={sharePopupOpen}
          setIsOpen={setSharePopupOpen}
          url={window.location.href}
        />
      )}

      {/* Add URLs Popup */}
      {showUrlsPopup && (
        <div className="urls-popup-page">
          <div className="urls-popup-content">
            <h3>Resource URLs</h3>
            <div className="urls-list">
              {product.URLS.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>Resource {index + 1}</span>
                  <span className="urls-popup-open-btn">Open</span>
                </a>
              ))}
            </div>
            <button
              className="urls-popup-close-btn"
              onClick={() => setShowUrlsPopup(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showEditPopup && (
        <AddProductPopup
          communityId={communityId}
          onClose={() => setShowEditPopup(false)}
          onProductUpdate={handleEditComplete}
          product={product}
        />
      )}

      <Footer />
    </div>
  );
};

export default ProductPage; 